import { useEffect, useRef } from "react"

const SIZE = 32
const RADIUS = SIZE / 2 - 2.5
const ARC_LENGTH = Math.PI * 1.3
const SPEED = 3 // radians per second
const COLOR = "#1976d2"
const TRACK_COLOR = "rgba(25, 118, 210, 0.2)"

function drawFrame(ctx: CanvasRenderingContext2D, img: HTMLImageElement | null, angle: number) {
  ctx.clearRect(0, 0, SIZE, SIZE)

  if (img) {
    ctx.save()
    ctx.beginPath()
    ctx.arc(SIZE / 2, SIZE / 2, RADIUS - 1.5, 0, Math.PI * 2)
    ctx.clip()
    const pad = SIZE / 2 - RADIUS + 3
    ctx.drawImage(img, pad, pad, SIZE - pad * 2, SIZE - pad * 2)
    ctx.restore()
  }

  ctx.beginPath()
  ctx.arc(SIZE / 2, SIZE / 2, RADIUS, 0, Math.PI * 2)
  ctx.strokeStyle = TRACK_COLOR
  ctx.lineWidth = 2.5
  ctx.stroke()

  const start = angle - Math.PI / 2
  ctx.beginPath()
  ctx.arc(SIZE / 2, SIZE / 2, RADIUS, start, start + ARC_LENGTH)
  ctx.strokeStyle = COLOR
  ctx.lineWidth = 2.5
  ctx.lineCap = "round"
  ctx.stroke()
}

export function useFaviconLoader(isLoading: boolean): void {
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isLoading) return

    const link = document.querySelector<HTMLLinkElement>("link[rel*='icon']")
    if (!link) return
    const originalHref = link.href

    const canvas = document.createElement("canvas")
    canvas.width = SIZE
    canvas.height = SIZE
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    let angle = 0
    let prev = 0
    let img: HTMLImageElement | null = null

    const tick = (ts: number) => {
      if (prev === 0) prev = ts
      angle += ((ts - prev) / 1000) * SPEED
      prev = ts
      drawFrame(ctx, img, angle)
      try {
        link.href = canvas.toDataURL()
      } catch {
        rafRef.current = null
        link.href = originalHref
        return
      }
      rafRef.current = requestAnimationFrame(tick)
    }

    const image = new Image()
    image.crossOrigin = "anonymous"
    image.onload = () => {
      img = image
    }
    image.src = originalHref

    rafRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
      link.href = originalHref
    }
  }, [isLoading])
}
